import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import {MatOption, MatSelect, MatSelectTrigger} from '@angular/material/select';
import {FormControl, FormsModule, ReactiveFormsModule} from '@angular/forms';
import {ALL_ID, SelectItem} from '@twpub/core/models';
import {NGXLogger} from 'ngx-logger';
import { NgClass } from '@angular/common';
import {MatFormField, MatLabel} from '@angular/material/form-field';
import {SelectAllNoneButtonsComponent} from './select-all-none-buttons/select-all-none-buttons.component';

@Component({
  selector: 'pub-multi-select',
  templateUrl: './multi-select.component.html',
  styleUrls: ['./multi-select.component.scss'],
  standalone: true,
  imports: [MatFormField, NgClass, MatLabel, MatSelect, FormsModule, ReactiveFormsModule, SelectAllNoneButtonsComponent, MatSelectTrigger, MatOption]
})
export class MultiSelectComponent implements OnInit, OnChanges {
  @Input() items: SelectItem[] = [];
  @Input() selectedValues?: string[] = [];
  @Input() multipleSelect: boolean = false;
  @Input() cssClassName?: string;
  @Input() label?: string;
  @Input() allSelectedLabel?: string;
  @Input() required: boolean = false;
  @Input() logger!: NGXLogger;

  @Output() selectValues = new EventEmitter<string[]>();

  @ViewChild('select') select!: MatSelect;

  selectControl = new FormControl<string[] | string>([]);

  allSelected: boolean = false;
  singleSelected: boolean = false;
  selectedText: string = '';
  private isProcessingChange = false; // Flag to prevent reentrant emissions

  ngOnInit(): void {
    this.updateFormControl();
    this.selectControl.valueChanges.subscribe((values) => {
      if (!this.isProcessingChange) {
        this.onSelectionChange(values);
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.updateFormControl();
  }

  private updateFormControl() {
    if (this.multipleSelect) {
      this.updateMultipleSelect();
    } else {
      this.updateSingleSelect();
    }
    this.updateDisplayText();
  }

  private updateMultipleSelect() {
    if (this.checkIfAllSelected()) {
      this.items.forEach(item => {
        item.selected = true;
        item.disabled = false;
      });
      this.selectControl.setValue(this.items.map(s => s.value));
      this.allSelected = true;
    } else {
      this.selectControl.setValue(this.selectedValues || []);
      this.singleSelected = this.selectedValues?.length === 1;
      this.toggleDisabledValues(this.items, this.selectedValues || []);
    }
  }

  private updateSingleSelect() {
    let selValue = this.selectedValues?.[0];
    if (!!selValue && !this.items.some(item => item.value === selValue)) {
      this.logger.warn(`Selected value ${selValue} not found in items ${this.items.map(i => i.value)}`);
      selValue = undefined;
    }
    const newValue = selValue || (this.allSelectedLabel && !this.required ? '' : this.items[0].value);
    this.selectControl.setValue(newValue);
  }

  private checkIfAllSelected(): boolean {
    const selLen = this.selectedValues?.length;
    return selLen === 0 || this.selectedValues?.[0] === ALL_ID || selLen === this.items?.length;
  }

  private onSelectionChange(values: string | string[] | null): void {
    if (!this.items.length) return;

    let selectedValues: string[];

    if (this.multipleSelect) {
      selectedValues = (values as string[]) || [];
      this.allSelected = selectedValues.length === this.items.length;
      this.singleSelected = selectedValues.length === 1;

      if (selectedValues.length === 0 && this.allSelected) {
        selectedValues = this.items.map(item => item.value);
      }
      const isAllSelected = selectedValues.length === this.items.length;
      const isNoneSelected = selectedValues.length === 0;

      if (isAllSelected) {
        this.allSelected = true;
      } else if (isNoneSelected) {
        selectedValues = [this.items[0].value];
        this.isProcessingChange = true;
        this.selectControl.setValue(selectedValues);
        this.isProcessingChange = false;
        this.allSelected = false;
      } else {
        selectedValues = selectedValues.filter(oid => this.items?.some(section => section.value === oid));
        this.allSelected = false;
      }
      this.toggleDisabledValues(this.items, selectedValues);
      this.selectedValues = selectedValues;
      this.selectValues.emit(this.allSelected ? [] : selectedValues);
    } else {
      selectedValues = values ? [values as string] : [];
      this.selectedValues = selectedValues;
      this.selectValues.emit(selectedValues);
    }
    this.updateDisplayText();
  }

  private toggleDisabledValues(items: SelectItem[], selectedValues: string[]): void {
    this.singleSelected = selectedValues.length === 1;
    if (this.singleSelected) {
      items.find(item => item.value === selectedValues[0])!.disabled = true;
    } else {
      items.forEach(item => item.disabled = false);
    }
  }

  toggleAllSelection(all: boolean = true): void {
    if (all) {
      this.selectControl.setValue(this.items.map(item => item.value));
    } else {
      this.selectControl.setValue([]);
    }
    // No need to emit here as valueChanges will handle it
  }

  private updateDisplayText(): void {
    if (!this.multipleSelect) {
      const value = this.selectControl.value as string;
      if (value === '') {
        this.selectedText = this.allSelectedLabel || '';
      } else {
        const selectedItem = this.items.find(item => item.value === value);
        this.selectedText = selectedItem?.name || '';
      }
    } else {
      if (this.allSelected && this.allSelectedLabel) {
        this.selectedText = this.allSelectedLabel;
        return;
      }

      const maxItemsToShow = 3;
      const selectedItems = this.items.filter(item =>
        this.selectedValues?.includes(item.value)
      );

      if (selectedItems.length <= maxItemsToShow) {
        this.selectedText = selectedItems.map(item => item.name).join(', ');
      } else {
        const displayItems = selectedItems.slice(0, maxItemsToShow);
        this.selectedText = `${displayItems.map(item => item.name).join(', ')} and ${selectedItems.length - maxItemsToShow} more`;
      }
    }
  }
}
